import { SRFBridge } from 'srf-native-bridge';

import { readMetaTagWithDefault } from '@fec/assets/js/utils/meta-tags.js';
import { SHOW_INITIAL, TICKER_STATUS } from './Ticker.jsx';
import Urn from '@fec/frontend/foundation/client/urn.js';
import ClientStorage from '@fec/frontend/foundation/client/storage.js';
import { PREVENT_LINK_HANDLING_ATTRIBUTE } from '@aron/common/bridge/main.js';
import {
  preventUnwantedScroll,
  supportsScrollAnchoring,
} from '@fec/assets/js/shame.js';

const SCROLL_MARGIN_TOP = 16;

const doTheScrolling = (element, behavior = 'smooth') => {
  const masthead = document.querySelector('.js-masthead');
  const mastheadHeight = masthead?.offsetHeight ?? 0;
  const tickerOffset = element.getBoundingClientRect().top;
  const top =
    window.scrollY + tickerOffset - mastheadHeight - SCROLL_MARGIN_TOP;

  // UX improvement: only scroll when the element we're scrolling to is out of sight "above" or below half of the screen
  // (or in other words: don't scroll if the element is visible in the upper half of the screen)
  // Aren't we considerate? :)
  const isOufOfViewAbove = top < window.scrollY;
  const isBelowHalfOfScreen = top - window.scrollY > window.innerHeight / 2;

  if (isOufOfViewAbove || isBelowHalfOfScreen) {
    window.scroll({ top, behavior });
  }
};

export const scrollToElement = (element) => {
  if (supportsScrollAnchoring) {
    doTheScrolling(element, 'smooth');
  } else {
    doTheScrolling(element, 'instant');
    requestAnimationFrame(() =>
      preventUnwantedScroll(element, () => doTheScrolling(element, 'instant')),
    );
  }
};

const serializeSlateNode = (node) => {
  if (typeof node.text !== 'undefined') {
    return node.text;
  }

  if (node.children) {
    return node.children.map((child) => serializeSlateNode(child)).join('');
  }

  return '';
};

export const generateSchemaOrgData = (startTime, messages) => {
  const previousElement = document.querySelector('#schema-data-script');
  if (previousElement) {
    previousElement.remove();
  }

  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('id', 'schema-data-script');

  const data = {
    '@context': 'https://schema.org',
    '@type': 'LiveBlogPosting',
    datePublished: startTime,
    liveBlogUpdate: [],
  };

  const headline = readMetaTagWithDefault('headline', false);
  if (headline) {
    data.headline = headline;
  }

  const description = readMetaTagWithDefault('description', false);
  if (description) {
    data.description = description;
  }

  const seoUrl = document.querySelector('link[rel="canonical"]')?.href;
  if (seoUrl) {
    data.url = seoUrl;
  }

  if (messages?.length > 0) {
    data.dateModified = messages[0].data.publishedAt;

    messages.forEach((message) => {
      data.liveBlogUpdate.push({
        '@type': 'BlogPosting',
        headline: message.data.title,
        articleBody: message.data.text
          .map((node) => serializeSlateNode(node))
          .filter((entry) => entry?.length > 0)
          .join(' '),
        datePublished: message.data.publishedAt,
      });
    });
  }

  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

export const generateStatusText = (label, startTime, t) => {
  if (label === 'newsticker' || label === 'kulturticker') {
    return {
      scheduled: t('Der Ticker startet um {{Uhrzeit}} Uhr', {
        Uhrzeit: `${startTime.getHours()}:${startTime
          .getMinutes()
          .toString()
          .padStart(2, '0')}`,
      }),
      live: t('Ticker läuft seit {{Uhrzeit_und_Datum}} Uhr', {
        Uhrzeit_und_Datum: `${startTime.toLocaleDateString(
          'de-CH',
        )}, ${startTime.getHours()}:${startTime
          .getMinutes()
          .toString()
          .padStart(2, '0')}`,
      }),
      closed: t('Der Ticker ist abgeschlossen'),
    };
  } else if (label === 'ticker') {
    return {
      scheduled: t('Der Ticker startet um {{Uhrzeit}} Uhr', {
        Uhrzeit: `${startTime.getHours()}:${startTime
          .getMinutes()
          .toString()
          .padStart(2, '0')}`,
      }),
      live: t('Live seit {{Uhrzeit_und_Datum}} Uhr', {
        Uhrzeit_und_Datum: `${startTime.toLocaleDateString(
          'de-CH',
        )}, ${startTime.getHours()}:${startTime
          .getMinutes()
          .toString()
          .padStart(2, '0')}`,
      }),
      closed: t('Der Ticker ist abgeschlossen'),
    };
  } else {
    return {
      scheduled: t('Der Chat startet um {{Uhrzeit}} Uhr', {
        Uhrzeit: `${startTime.getHours()}:${startTime
          .getMinutes()
          .toString()
          .padStart(2, '0')}`,
      }),
      live: t('Automatische Aktualisierung'),
      closed: t('Chat-Protokoll'),
    };
  }
};

export const handleLinkClick = (event, messages, callback) => {
  // should click be prevented? yes, if we have messages and a link (or a
  // descendant of one) was clicked and the link has an anchor and the
  // "anchored item" exists in the current liveticker.
  if (messages.length === 0) {
    return;
  }

  const linkElement = event.target.closest('a');
  if (!linkElement) {
    return;
  }

  const anchor = getAnchorFromUrl(linkElement.href);
  if (!anchor) {
    return;
  }

  if (messages.find(({ id }) => String(id) === String(anchor))) {
    event.preventDefault();
    // prevent handling the links via the bridge
    linkElement.dataset[PREVENT_LINK_HANDLING_ATTRIBUTE] = true;
    callback(anchor);
  }
};

export const getTickerStatus = (active, startTime) => {
  if (active) {
    if (startTime > new Date()) {
      return TICKER_STATUS.SCHEDULED;
    } else {
      return TICKER_STATUS.LIVE;
    }
  } else {
    return TICKER_STATUS.CLOSED;
  }
};

export const makeMapFromArray = (itemArray) => {
  const itemMap = new Map();
  itemArray.forEach((item) => itemMap.set(item.id, item));
  return itemMap;
};

export const makeArrayFromMap = (itemMap) => {
  return [...itemMap.values()]
    .filter((item) => item.data !== null)
    .sort((a, b) => (a.data.timestamp > b.data.timestamp ? -1 : 1));
};

export const addNewMessages = (previousItems, newMessages) => {
  // make a map out of the old messages to be able to override duplicates (newer wins)
  const oldItemMap = makeMapFromArray(previousItems);

  // add new item/override old ones with the same id
  newMessages.forEach((message) => {
    const newItem = makeTickerItemFromAblyMessage(message);
    oldItemMap.set(newItem.id, newItem);
  });

  // return as array, filter out empty values and order by timestamp
  return makeArrayFromMap(oldItemMap);
};

export const addNewMessage = (previousTickerItems, newMessage) => {
  // make a map out of the old messages to be able to override duplicates (newer wins)
  const oldItemMap = makeMapFromArray(previousTickerItems);

  const newItem = makeTickerItemFromAblyMessage(newMessage);

  // add new message/override old message with the same id
  oldItemMap.set(newItem.id, newItem);

  // return as array, filter out empty values and order by timestamp
  return makeArrayFromMap(oldItemMap);
};

export const removeMessage = (previousTickerItems, id) => {
  const oldTickerMap = makeMapFromArray(previousTickerItems);

  if (oldTickerMap.has(id)) {
    oldTickerMap.delete(id);
    return makeArrayFromMap(oldTickerMap);
  }

  // return original items if none was removed
  return previousTickerItems;
};

export const remakeShownMessages = (messages, previousShownMessages) => {
  // shortcut #1: if no messages were previously shown, just return the first 5 messages
  if (previousShownMessages.length === 0) {
    return messages.slice(0, SHOW_INITIAL);
  }
  // shortcut #2: if no messages will be shown, skip the whole stuff below
  if (messages.length === 0) {
    return [];
  }

  // first we need to find the top message that is being show, which exists in
  // the new messages, because any number of them could have been deleted.
  const topShownMessageThatExistsInMessages = previousShownMessages.find(
    (shownMessage) =>
      messages.find((message) => shownMessage.id === message.id),
  );
  let topMessageId = topShownMessageThatExistsInMessages?.id;

  // if none of our shown messages exist in the messages anymore:
  // as a last resort, we take the first message that is older than the newest
  // one that is being shown
  if (!topShownMessageThatExistsInMessages) {
    const newestMessageThatIsOlderThanNewestShownMessage = messages.find(
      (message) =>
        message.data.timestamp <= previousShownMessages[0].data.timestamp,
    );

    if (newestMessageThatIsOlderThanNewestShownMessage) {
      topMessageId = newestMessageThatIsOlderThanNewestShownMessage.id;
    } else {
      // absolute worst case: start at the beginning
      topMessageId = messages[0].id;
    }
  }

  const messageIndexTop = messages.findIndex(({ id }) => id === topMessageId);

  const bottomShownMessageThatExistsInMessages = previousShownMessages
    .reverse()
    .find((shownMessage) =>
      messages.find((message) => shownMessage.id === message.id),
    );
  let bottomMessageId = bottomShownMessageThatExistsInMessages?.id;

  // if none of our shown messages exist in the messages anymore:
  // as a last resort, we take the first message that is older than the last shown message
  if (!bottomShownMessageThatExistsInMessages) {
    const oldestShown = previousShownMessages[previousShownMessages.length - 1];
    const newestMessageThatIsOlderThanOldestShownMessage = messages.find(
      (message) => message.data.timestamp <= oldestShown.data.timestamp,
    );

    if (newestMessageThatIsOlderThanOldestShownMessage) {
      bottomMessageId = newestMessageThatIsOlderThanOldestShownMessage.id;
    } else {
      // absolute worst case: show as many as we did before ¯\_(ツ)_/¯
      const lastIndex = Math.min(
        messageIndexTop + previousShownMessages.length - 1,
        messages.length - 1,
      );
      bottomMessageId = messages[lastIndex].id;
    }
  }

  const messageIndexBottom = messages.findIndex(
    ({ id }) => id === bottomMessageId,
  );

  // make sure that messageIndexBottom isn't less than messageIndexTop
  return messages.slice(
    messageIndexTop,
    Math.max(messageIndexTop, messageIndexBottom) + 1,
  );
};

export const makeTickerItemFromAblyMessage = (message) => ({
  id: message.data.id,
  timestamp: message.data.timestamp,
  data: message.data,
});

export const getAnchorFromUrl = (url) => {
  try {
    const typedUrl = new URL(url);
    const searchParams = typedUrl?.searchParams;
    const anchor = searchParams?.get('anchor');

    return getItemIdFromAnchor(anchor);
  } catch (e) {
    // no valid URL
  }

  return null;
};

export const getItemToJumpTo = () => {
  if (SRFBridge.isRunningInApp()) {
    const anchorValue = ClientStorage.getItem('srf:anchor');
    ClientStorage.removeItem('srf:anchor');
    return getItemIdFromAnchor(anchorValue);
  } else {
    return getAnchorFromUrl(window.location.href);
  }
};

export const getItemIdFromAnchor = (anchor) => {
  if (Urn.isValid(anchor) && Urn.getType(anchor) === 'livetickerItem') {
    return Urn.getId(anchor);
  } else {
    return null;
  }
};

export const getNrOfNewerMessages = (shownMessages, messages) => {
  if (shownMessages.length > 0 && messages.length > 0) {
    // careful, a ticker item that's being shown could have been deleted!

    const idOfNewestShownMessage = shownMessages[0].id;
    const indexOfNewestShownIndexInMessages = messages.findIndex(
      ({ id }) => id === idOfNewestShownMessage,
    );

    if (indexOfNewestShownIndexInMessages >= 0) {
      return indexOfNewestShownIndexInMessages;
    }

    // There's no message being shown that exists in the "big" stack.
    // Let's use the timestamp which is a reasonable fallback:
    // We search for the first message that's older than the newest shown
    // message, which means that the message _after_ this one is the next
    // message to be shown. Hopefully.
    const timestampOfTopShownMessage = shownMessages[0].timestamp;
    const indexOfFirstOlderMessage = messages.findIndex(
      ({ timestamp }) => timestamp >= timestampOfTopShownMessage,
    );

    if (indexOfFirstOlderMessage > 0) {
      return indexOfFirstOlderMessage - 1;
    }
  }

  // fallback: no new messages
  return 0;
};

export const getNrOfOlderMessages = (shownMessages, messages) => {
  if (shownMessages.length > 0 && messages.length > 0) {
    const idOfLastShownMessage = shownMessages[shownMessages.length - 1].id;
    const indexOfLastShownIndexInMessages = messages.findIndex(
      ({ id }) => id === idOfLastShownMessage,
    );

    if (indexOfLastShownIndexInMessages >= 0) {
      return messages.length - 1 - indexOfLastShownIndexInMessages;
    }

    // If the last shown message doesn't exist in the "big" stack,
    // use the timestamp as a fallback.
    const timestampOfBottomShownMessage =
      shownMessages[shownMessages.length - 1].timestamp;
    const indexOfFirstNewerMessage = messages.findIndex(
      ({ timestamp }) => timestamp <= timestampOfBottomShownMessage,
    );

    if (indexOfFirstNewerMessage >= 0) {
      return messages.length - indexOfFirstNewerMessage;
    }
  }

  // fallback: no older messages
  return 0;
};
